import React from 'react'
import { useHistory } from '~/history'
import { Breadcrumbs } from '~/ui/app/navigation'
import { observer } from '~/ui/component'
import { BackButton, BrandedLogo, HBox, Label, Spinner, Tappable, VBox } from '~/ui/components'
import { createUseStyles, layout } from '~/ui/styling'
import AppLayoutContext from './AppLayoutContext'
import { projectStore } from '~/stores'
import DrawerToggle from '~/ui/app/drawer/DrawerToggle'
import { Breadcrumb } from '~/ui/app/navigation/types'

const AppHeader = observer('AppHeader', () => {

  const {breadcrumbs, fetchStatus, ActionsComponent} = React.useContext(AppLayoutContext)

  const history = useHistory()

  const {project} = projectStore

  const parentBreadcrumb = React.useMemo(() => {
    if (history.index > 0) {
      return breadcrumbs[history.index -1]
    } else if (breadcrumbs.length > 1) {
      return breadcrumbs[breadcrumbs.length - 2]
    }
  }, [breadcrumbs, history.index])

  const canUseHistoryStack = history.stack.length > 1

  const onBreadcrumbTap = React.useCallback((breadcrumb: Breadcrumb, index: number, event: React.SyntheticEvent) => {
    if (canUseHistoryStack || breadcrumb.href == null) { return }
    history.reset(breadcrumb.href)
  }, [canUseHistoryStack, history])

  const backHref    = parentBreadcrumb?.href
  const backCaption = parentBreadcrumb?.caption

  const canGoBack = backHref != null || history.index > 0

  const goBack = React.useCallback((event: React.SyntheticEvent) => {
    event.preventDefault()
    if (backHref != null) {
      history.reset(backHref)
    } else {
      history.goBack()
    }
  }, [history, backHref])

  const caption = React.useMemo(() => {
    return breadcrumbs[history.index]?.caption
  }, [breadcrumbs, history])

  const {collapsed: isMobile} = React.useContext(AppLayoutContext)

  const drawerEnabled  = project?.feature('drawer')?.enabled && isMobile
  const showScreenInfo = !isMobile || canGoBack

  const $ = useStyles()

  //------
  // Rendering

  function render() {
    if (!isMobile && !canGoBack && ActionsComponent == null) { return null }
    return (
      <HBox classNames={[$.AppHeader, {mobile: isMobile}]} paddingHorizontal={layout.padding.m} gap={isMobile ? layout.padding.inline.m : layout.padding.m}>
        {showScreenInfo ? (
          <>
            {renderBackButton()}
            {isMobile ? renderCaption(): renderBreadcrumbs()}
          </>
        ) : (
          <>
            <HBox gap={layout.padding.m} flex>
              {drawerEnabled && <DrawerToggle/>}
              <Tappable href='/'>
                <BrandedLogo/>
              </Tappable>
            </HBox>
          </>
        )}
        {ActionsComponent != null && <ActionsComponent/>}
      </HBox>
    )
  }

  function renderBackButton() {
    if (!canGoBack) { return null }

    return (
      <BackButton
        href={backHref}
        caption={isMobile ? backCaption : null}
        onTap={goBack}
      />
    )
  }

  function renderCaption() {
    return (
      <Label bold small flex>
        {caption}
      </Label>
    )
  }

  function renderBreadcrumbs() {
    return (
      <VBox flex>
        {fetchStatus === 'fetching' ? (
          <Spinner size={8}/>
        ) : (
          <Breadcrumbs
            breadcrumbs={breadcrumbs}
            onTap={onBreadcrumbTap}
          />
        )}
      </VBox>

    )
  }

  return render()

})

export default AppHeader

export const height = layout.barHeight.l + 2

const useStyles = createUseStyles(theme => ({
  AppHeader: {
    height:   height,
    borderBottom: [2, 'solid', theme.border.dimmer],

    '&.mobile': {
      backgroundColor: theme.bg.alt,
      borderTop: [1, 'solid', theme.border.dimmer],
      borderBottom: [1, 'solid', theme.border.dimmer],
    },
  },
}))